const TRACE_DURATION_SECONDS = 1.8;
const FILL_DURATION_SECONDS = 0.45;
// Fraction of the stagger window relative to total draw duration.
// e.g. 0.5 means the last shape starts drawing at 50% of the total time.
const STAGGER_SPREAD = 0.5;
// Exponent for duration scaling relative to shape size. 1 = fully linear.
// Values below 1 give small shapes a longer draw time relative to their size.
const DURATION_EXPONENT = 0.6;
const DEFAULT_TRACE_WIDTH = 2.25;
const DRAWABLE_SELECTOR = 'path, circle, rect, ellipse, line, polyline, polygon';

function parseNumber(value) {
  if (!value) {
    return 0;
  }

  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Returns the arc length of a shape — used for stroke-dasharray/dashoffset. */
function getDashLength(shape) {
  if (typeof shape.getTotalLength === 'function') {
    const total = shape.getTotalLength();
    if (Number.isFinite(total) && total > 0) {
      return total;
    }
  }

  const tag = shape.tagName.toLowerCase();
  if (tag === 'rect') {
    const w = parseNumber(shape.getAttribute('width'));
    const h = parseNumber(shape.getAttribute('height'));
    return Math.max(1, 2 * (w + h));
  }

  if (tag === 'circle') {
    const r = parseNumber(shape.getAttribute('r'));
    return Math.max(1, 2 * Math.PI * r);
  }

  if (tag === 'ellipse') {
    const rx = parseNumber(shape.getAttribute('rx'));
    const ry = parseNumber(shape.getAttribute('ry'));
    const h = Math.pow(rx - ry, 2) / Math.pow(rx + ry || 1, 2);
    const circumference = Math.PI * (rx + ry) * (1 + (3 * h) / (10 + Math.sqrt(4 - (3 * h))));
    return Math.max(1, circumference);
  }

  if (tag === 'line') {
    const x1 = parseNumber(shape.getAttribute('x1'));
    const y1 = parseNumber(shape.getAttribute('y1'));
    const x2 = parseNumber(shape.getAttribute('x2'));
    const y2 = parseNumber(shape.getAttribute('y2'));
    return Math.max(1, Math.hypot(x2 - x1, y2 - y1));
  }

  try {
    const box = shape.getBBox();
    return Math.max(1, 2 * (box.width + box.height));
  } catch {
    return 1;
  }
}

/**
 * Returns the visual footprint (bounding box diagonal) of a shape.
 * Used for proportional duration — avoids overly-long durations driven by
 * mathematically complex paths that have a small visible area.
 */
function getTimingLength(shape) {
  try {
    const box = shape.getBBox();
    const diag = Math.hypot(box.width, box.height);
    if (diag > 0) {
      return diag;
    }
  } catch { /* fall through */ }
  // Fallback: arc length
  return getDashLength(shape);
}

function getSvgPaint(shape) {
  const stroke = shape.getAttribute('stroke');
  if (stroke && stroke !== 'none') {
    return stroke;
  }

  const fill = shape.getAttribute('fill');
  if (fill && fill !== 'none') {
    return fill;
  }

  const style = window.getComputedStyle(shape);
  if (style.stroke && style.stroke !== 'none') {
    return style.stroke;
  }
  if (style.fill && style.fill !== 'none') {
    return style.fill;
  }

  return '#ffffff';
}

function getLuminance(color) {
  const hex = color.trim().match(/^#([0-9a-f]{6})$/i);
  if (hex) {
    const value = hex[1];
    const r = parseInt(value.slice(0, 2), 16) / 255;
    const g = parseInt(value.slice(2, 4), 16) / 255;
    const b = parseInt(value.slice(4, 6), 16) / 255;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  const rgb = color.trim().match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const parts = rgb[1].split(',').map((part) => parseFloat(part.trim()));
    const r = (parts[0] || 0) / 255;
    const g = (parts[1] || 0) / 255;
    const b = (parts[2] || 0) / 255;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  return 1;
}

function getTraceColor(shape) {
  return getSvgPaint(shape);
}

function isValidCssColor(value) {
  return typeof value === 'string' && value.trim() !== '' && CSS.supports('color', value.trim());
}

/**
 * Generates SVG polygon points for a star centered at (cx, cy).
 * @param {number} cx
 * @param {number} cy
 * @param {number} outerR - outer radius
 * @param {number} innerR - inner radius
 * @param {number} numPoints - number of star points
 */
function generateStarPoints(cx, cy, outerR, innerR, numPoints) {
  const pts = [];
  for (let i = 0; i < numPoints * 2; i++) {
    const angle = (i * Math.PI) / numPoints - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    pts.push(`${(cx + r * Math.cos(angle)).toFixed(3)},${(cy + r * Math.sin(angle)).toFixed(3)}`);
  }
  return pts.join(' ');
}

/**
 * Creates a star polygon that rides along a path using CSS offset-path.
 * Only works for <path> elements (others don't have a `d` attribute).
 * @param {SVGPathElement} shape
 * @param {string} color
 * @param {number} strokeWidth - resolved trace stroke width in SVG units
 * @param {number} traceDuration
 * @param {string|null} [pathDOverride] - use this `d` string instead of shape's attribute
 * @returns {SVGPolygonElement|null}
 */
function createTraceTip(shape, color, strokeWidth, traceDuration, pathDOverride = null) {
  if (shape.tagName.toLowerCase() !== 'path') return null;
  const pathD = pathDOverride || shape.getAttribute('d');
  if (!pathD) return null;

  const outerR = strokeWidth * 2.8;
  const innerR = outerR * 0.42;

  const tip = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  tip.classList.add('draw-trace-tip');
  tip.setAttribute('points', generateStarPoints(0, 0, outerR, innerR, 5));
  tip.setAttribute('fill', color);
  // Sanitise path data for CSS path() — collapse whitespace
  const safePath = pathD.replace(/\s+/g, ' ').trim();
  tip.style.offsetPath = `path('${safePath}')`;
  tip.style.offsetDistance = '0%';
  tip.style.offsetRotate = '0deg';
  tip.style.setProperty('--trace-delay', '0s');
  tip.style.setProperty('--trace-duration', `${traceDuration}s`);
  return tip;
}

/**
 * Splits a path `d` string into individual subpath strings (each starting with M/m).
 * Returns the subpath with the greatest arc length, measured via a temporary DOM element.
 * Returns { d, isMulti, length } where isMulti=false means only one subpath existed.
 */
function getLongestSubpath(d) {
  const subpaths = d.trim().split(/(?=[Mm])/).map((s) => s.trim()).filter(Boolean);
  if (subpaths.length <= 1) return { d, isMulti: false, length: 0 };

  const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  tempSvg.setAttribute('style', 'position:absolute;visibility:hidden;pointer-events:none;width:0;height:0');
  const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  tempSvg.appendChild(tempPath);
  document.body.appendChild(tempSvg);

  let longestD = subpaths[0];
  let longestLen = 0;
  let longestIdx = 0;
  for (let i = 0; i < subpaths.length; i++) {
    const sub = subpaths[i];
    tempPath.setAttribute('d', sub);
    try {
      const len = tempPath.getTotalLength();
      if (len > longestLen) { longestLen = len; longestD = sub; longestIdx = i; }
    } catch { /* skip */ }
  }

  document.body.removeChild(tempSvg);

  // If the winning subpath is not the first and starts with a relative 'm', its coordinates
  // are relative to the start of the previously-closed subpath (after 'z'). When placed in a
  // standalone element the relative 'm' would be resolved from (0,0) instead, rendering it at
  // the wrong position (typically the top-left corner). Convert it to an absolute 'M'.
  if (longestIdx > 0 && /^m[\s,]/i.test(longestD) && longestD[0] === 'm') {
    // Accumulate the absolute start point by tracking the initial M/m of each preceding subpath.
    // Each preceding subpath is assumed to end with 'z', which resets the current point back to
    // that subpath's own start — so only the initial move of each subpath matters here.
    let cx = 0;
    let cy = 0;
    for (let i = 0; i < longestIdx; i++) {
      const match = subpaths[i].match(/^([Mm])[\s,]*([-\d.e]+)[\s,]+([-\d.e]+)/i);
      if (match) {
        if (match[1] === 'M') {
          cx = parseFloat(match[2]);
          cy = parseFloat(match[3]);
        } else {
          cx += parseFloat(match[2]);
          cy += parseFloat(match[3]);
        }
      }
    }
    longestD = longestD.replace(/^m[\s,]*([-\d.e]+)[\s,]+([-\d.e]+)/i, (_, dx, dy) =>
      `M ${cx + parseFloat(dx)},${cy + parseFloat(dy)}`
    );
  }

  return { d: longestD, isMulti: true, length: longestLen };
}

function getTraceOptions(svg) {
  // data-trace-speed: multiplier controlling draw speed (default 1.0, higher = faster).
  // The longest shape always draws in TRACE_DURATION_SECONDS / speed seconds.
  const speedAttr = parseFloat(svg.dataset.traceSpeed || '');
  const speed = Number.isFinite(speedAttr) && speedAttr > 0 ? speedAttr : 1;

  const colorAttr = (svg.dataset.traceColor || '').trim();
  const color = isValidCssColor(colorAttr) ? colorAttr : null;

  const widthAttr = parseFloat(svg.dataset.traceWidth || '');
  const width = Number.isFinite(widthAttr) && widthAttr > 0 ? widthAttr : null;

  // data-trace-draw="simultaneous" (default) | "staggered" | "sequential"
  const drawStyleRaw = (svg.dataset.traceDraw || '').trim();
  const drawStyle = ['staggered', 'sequential'].includes(drawStyleRaw) ? drawStyleRaw : 'simultaneous';

  // data-sequential-draw-delay: 0–1 ratio (default 1).
  // 0 = all lines start together, 1 = each line starts when the previous finishes.
  // Fractional values allow partial overlap.
  const seqDelayAttr = parseFloat(svg.dataset.sequentialDrawDelay || '');
  const sequentialDrawDelay = Number.isFinite(seqDelayAttr) && seqDelayAttr >= 0 && seqDelayAttr <= 1
    ? seqDelayAttr
    : 1;

  // data-trace-shapes="draw" (default) | "fade"
  // draw: shapes reveal after traces finish drawing
  // fade: shapes fade in immediately while traces draw; traces fade out after drawing completes
  const shapeStyle = (svg.dataset.traceShapes || '').trim() === 'fade' ? 'fade' : 'draw';

  // data-show-star: presence enables the star tip on traced paths
  const showStar = 'showStar' in svg.dataset;

  // data-fill-offset: ratio (0–1) controlling when the fill/reveal starts relative to trace completion.
  // 1 = fill starts when trace finishes (default). 0.9 = fill starts at 90% through the trace duration.
  const fillOffsetAttr = parseFloat(svg.dataset.fillOffset || '');
  const fillOffset = Number.isFinite(fillOffsetAttr) && fillOffsetAttr >= 0 && fillOffsetAttr <= 1 ? fillOffsetAttr : 1;

  return { speed, color, width, drawStyle, sequentialDrawDelay, shapeStyle, showStar, fillOffset };
}

function createTracePath(shape, traceOptions, maxLength) {
  const trace = shape.cloneNode(false);

  trace.classList.add('draw-trace-path');
  trace.classList.remove('draw-base-shape');
  trace.removeAttribute('id');
  trace.setAttribute('fill', 'none');
  trace.setAttribute('stroke', traceOptions.color || getTraceColor(shape));
  trace.setAttribute(
    'stroke-width',
    traceOptions.width !== null
      ? String(traceOptions.width)
      : (shape.getAttribute('stroke-width') || String(DEFAULT_TRACE_WIDTH))
  );
  trace.setAttribute('stroke-linecap', shape.getAttribute('stroke-linecap') || 'round');
  trace.setAttribute('stroke-linejoin', shape.getAttribute('stroke-linejoin') || 'round');

  // For path elements with multiple subpaths, only trace the longest one.
  let longestD = null;
  let longestDashLength = null;
  if (shape.tagName.toLowerCase() === 'path') {
    const raw = shape.getAttribute('d') || '';
    const result = getLongestSubpath(raw);
    if (result.isMulti) {
      longestD = result.d;
      longestDashLength = result.length;
      trace.setAttribute('d', longestD);
    }
  }

  const dashLength = longestDashLength !== null ? longestDashLength : getDashLength(shape);
  const timingLength = getTimingLength(shape);
  const minDurationSeconds = 0.18;
  const baseDuration = TRACE_DURATION_SECONDS / traceOptions.speed;
  const relativeDuration = maxLength > 0
    ? Math.pow(timingLength / maxLength, DURATION_EXPONENT) * baseDuration
    : baseDuration;
  const traceDuration = Math.max(minDurationSeconds, relativeDuration);

  trace.style.strokeDasharray = String(dashLength);
  trace.style.strokeDashoffset = String(dashLength);
  trace.style.setProperty('--trace-delay', '0s');
  trace.style.setProperty('--trace-duration', `${traceDuration}s`);

  const resolvedColor = traceOptions.color || getTraceColor(shape);
  const resolvedWidth = traceOptions.width !== null
    ? traceOptions.width
    : parseNumber(shape.getAttribute('stroke-width') || String(DEFAULT_TRACE_WIDTH));
  const tip = traceOptions.showStar
    ? createTraceTip(shape, resolvedColor, resolvedWidth, traceDuration, longestD)
    : null;

  return { trace, tip, traceDuration };
}

export function animateSVGs() {
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('.draw-me').forEach((svg) => {
      svg.classList.add('start-anim');
    });
    document.querySelectorAll('.scroll-trigger').forEach((svg) => {
      svg.classList.add('is-visible');
    });
    return;
  }

  /** @type {WeakMap<Element, {duration: number}>} */
  const svgFillTimers = new WeakMap();

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const svgEl = entry.target;
        svgEl.classList.add('start-anim');
        const opts = svgFillTimers.get(svgEl);

        const fadeTrace = (trace) => {
          trace.style.strokeDashoffset = '0';
          trace.style.animation = 'none';
          requestAnimationFrame(() => {
            trace.style.transition = `opacity ${FILL_DURATION_SECONDS}s ease-in-out`;
            trace.style.opacity = '0';
          });
        };

        const fadeTip = (tip) => {
          if (!tip) return;
          tip.style.animation = 'none';
          requestAnimationFrame(() => {
            tip.style.transition = `opacity ${FILL_DURATION_SECONDS}s ease-in-out`;
            tip.style.opacity = '0';
          });
        };

        if (opts && opts.perTraceFades) {
          // staggered+draw: fill-ready fires immediately; each trace/shape is
          // timed individually so shapes appear right after their own trace finishes.
          opts.perTraceFades.forEach(({ trace, tip, fadeAt }) => {
            setTimeout(() => {
              fadeTrace(trace);
              fadeTip(tip);
            }, fadeAt * 1000);
          });
          svgEl.classList.add('fill-ready');
        } else if (opts && opts.fillDelay !== null) {
          // Simultaneous draw mode: batch-fade all traces at fillDelay.
          setTimeout(() => {
            svgEl.querySelectorAll('.draw-trace-path').forEach(fadeTrace);
            svgEl.querySelectorAll('.draw-trace-tip').forEach(fadeTip);
            requestAnimationFrame(() => {
              svgEl.classList.add('fill-ready');
            });
          }, opts.fillDelay * 1000);
        }

        observer.unobserve(svgEl);
      }
    });
  }, { threshold: 1.0 });

  const drawMeSvgs = document.querySelectorAll('.draw-me');

  drawMeSvgs.forEach((svg) => {
    svg.querySelectorAll('.draw-trace-path, .draw-trace-tip').forEach((el) => el.remove());
    svg.classList.remove('fill-ready', 'trace-draw-simultaneous', 'trace-draw-staggered', 'trace-draw-sequential', 'trace-shapes-draw', 'trace-shapes-fade');

    const shapes = svg.querySelectorAll(`${DRAWABLE_SELECTOR}:not(.draw-trace-path)`);
    const traceOptions = getTraceOptions(svg);
    const timingLengths = Array.from(shapes, (shape) => getTimingLength(shape));
    const maxLength = timingLengths.reduce((max, l) => Math.max(max, l), 0);
    // Duration the longest shape will take to draw at this speed
    const totalDuration = TRACE_DURATION_SECONDS / traceOptions.speed;

    // Add mode classes so CSS knows which style to apply
    svg.classList.add(`trace-draw-${traceOptions.drawStyle}`, `trace-shapes-${traceOptions.shapeStyle}`);

    // For fade mode: --fill-duration is the per-shape fade duration (FILL_DURATION_SECONDS default,
    // overridden per shape via --shape-fill-duration in staggered fade).
    // For draw mode: --fill-duration is the short crossfade.
    svg.style.setProperty('--trace-duration', `${totalDuration}s`);
    svg.style.setProperty('--fill-duration', `${FILL_DURATION_SECONDS}s`);
    // --trace-fade-duration is always the short fade (used to fade-out traces in fade-shapes mode)
    svg.style.setProperty('--trace-fade-duration', `${FILL_DURATION_SECONDS}s`);

    // Create trace paths, collect per-shape durations.
    // In fade mode there are no traces — shapes fade in purely via opacity on start-anim.
    // Shapes with no fill (stroke-only "lines") are not hidden — they stay permanently visible
    // and only get a temporary trace drawn on top of them.
    const traceResults = [];
    shapes.forEach((shape) => {
      const fill = shape.getAttribute('fill') || window.getComputedStyle(shape).fill;
      const isLine = !fill || fill === 'none';

      shape.classList.add('draw-base-shape');

      if (traceOptions.shapeStyle === 'fade') {
        // fade mode: only hide-and-reveal filled shapes; lines already visible
        if (!isLine) {
          // shape delay/duration handled below
        }
        return;
      }

      const { trace, tip, traceDuration } = createTracePath(shape, traceOptions, maxLength);
      traceResults.push({ shape, trace, tip, traceDuration, isLine });
      shape.parentNode.appendChild(trace);
      if (tip) shape.parentNode.appendChild(tip);
    });

    // Apply stagger/sequential delays and compute max finish time
    let maxFinishTime = totalDuration;
    let staggerDelays = traceResults.map(() => 0);
    if (traceOptions.drawStyle === 'staggered' && traceResults.length > 1) {
      // Sort longest shapes first so they start drawing early; shorter shapes start later.
      traceResults.sort((a, b) => b.traceDuration - a.traceDuration);

      // Pass 1: assign trace delays and find the latest finish time
      staggerDelays = traceResults.map((_, i) =>
        (i / traceResults.length) * STAGGER_SPREAD * totalDuration
      );
      let maxFT = 0;
      traceResults.forEach(({ trace, tip, traceDuration }, i) => {
        trace.style.setProperty('--trace-delay', `${staggerDelays[i]}s`);
        if (tip) tip.style.setProperty('--trace-delay', `${staggerDelays[i]}s`);
        maxFT = Math.max(maxFT, staggerDelays[i] + traceDuration);
      });
      maxFinishTime = maxFT;

      // Pass 2: set per-shape reveal delays (draw mode only; fade mode has no traces)
      traceResults.forEach(({ shape, traceDuration }, i) => {
        const traceDelay = staggerDelays[i];
        // draw: fill-ready fires at t=0, so shape delay = fill-offset ratio * trace finish time
        const shapeDelay = (traceDelay + traceDuration) * traceOptions.fillOffset;
        shape.style.setProperty('--shape-delay', `${shapeDelay.toFixed(4)}s`);
      });
    } else if (traceOptions.drawStyle === 'sequential' && traceResults.length > 1) {
      // Sequential: each trace starts after (sequentialDrawDelay * previous trace duration).
      // 0 = all together, 1 = fully one-after-another.
      let cursor = 0;
      let maxFT = 0;
      traceResults.forEach(({ trace, tip, traceDuration }, i) => {
        staggerDelays[i] = cursor;
        trace.style.setProperty('--trace-delay', `${cursor.toFixed(4)}s`);
        if (tip) tip.style.setProperty('--trace-delay', `${cursor.toFixed(4)}s`);
        maxFT = Math.max(maxFT, cursor + traceDuration);
        cursor += traceOptions.sequentialDrawDelay * traceDuration;
      });
      maxFinishTime = maxFT;

      // Per-shape reveal delays: fill-ready fires at maxFinishTime, all shapes reveal together
      // (no --shape-delay needed; they all use the default 0s)
    }

    // In fade mode, set per-shape delays for staggered/sequential fade-in (all shapes)
    if (traceOptions.shapeStyle === 'fade' && traceOptions.drawStyle === 'staggered' && shapes.length > 1) {
      const allShapes = Array.from(shapes);
      allShapes.forEach((shape, i) => {
        const delay = (i / allShapes.length) * totalDuration;
        shape.style.setProperty('--shape-delay', `${delay.toFixed(4)}s`);
        shape.style.setProperty('--shape-fill-duration', `${FILL_DURATION_SECONDS}s`);
      });
    } else if (traceOptions.shapeStyle === 'fade' && traceOptions.drawStyle === 'sequential' && shapes.length > 1) {
      // Sequential fade: all shapes fade in at once (no per-shape delay needed)
    }

    svg.style.setProperty('--total-trace-duration', `${maxFinishTime}s`);

    // Compute when to add fill-ready and how to handle trace fades:
    // - fade mode: no traces; fill-ready not needed (shapes handled by CSS on start-anim)
    // - staggered+draw: fill-ready fires immediately; per-trace fades stored separately
    // - simultaneous+draw: wait for full trace duration, then fade traces and reveal shapes
    let fillDelay;
    /** @type {Array<{trace: Element, tip: Element|null, fadeAt: number}>|null} */
    let perTraceFades = null;

    if (traceOptions.shapeStyle === 'fade') {
      fillDelay = null; // CSS handles shape fade via start-anim; no fill-ready needed
    } else if (traceOptions.drawStyle === 'staggered') {
      fillDelay = 0;
      perTraceFades = traceResults.map(({ trace, tip, traceDuration }, i) => ({
        trace,
        tip,
        fadeAt: (staggerDelays[i] + traceDuration) * traceOptions.fillOffset,
      }));
    } else {
      fillDelay = maxFinishTime * traceOptions.fillOffset;
    }

    if (shapes.length === 0) {
      // console.log('[svg-animate] no shapes found in', svg);
    } else {
      svgFillTimers.set(svg, { fillDelay, perTraceFades });
      // console.log('[svg-animate] setup', shapes.length, 'shapes |', traceOptions.drawStyle, '+', traceOptions.shapeStyle, '| fillDelay:', fillDelay !== null ? fillDelay.toFixed(3) + 's' : 'none (fade)', '| maxFinishTime:', maxFinishTime.toFixed(3) + 's', svg);
    }

    observer.observe(svg);
  });

  const svgObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        svgObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.2 });

  const scrollTriggerSvgs = document.querySelectorAll('.scroll-trigger');
  scrollTriggerSvgs.forEach((svg) => {
    svgObserver.observe(svg);
  });

  // draw-me-original: simple staggered stroke draw, no fill reveal logic
  const originalObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('start-anim');
        originalObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.2 });

  document.querySelectorAll('.draw-me-original').forEach((svg) => {
    svg.querySelectorAll('path').forEach((path, index) => {
      const length = path.getTotalLength();
      path.style.strokeDasharray = length;
      path.style.strokeDashoffset = length;
      path.style.animationDelay = `${index * 0.08}s`;
    });
    originalObserver.observe(svg);
  });
}